// Attaques des monstres, leurs effets et leur apprentissage. Partagé par le serveur (moteur de combat),
// le jeu (menus, fiche) et l'administration.
import type { Element } from './elements.ts';

/** `damage` : inflige des dégâts (puis ses effets) ; `status` : n'agit que par ses effets. */
export type MoveCategory = 'damage' | 'status';

export type StatusCondition = 'poison' | 'burn' | 'paralysis' | 'sleep' | 'freeze';

export const STATUSES: Readonly<Record<StatusCondition, { label: string; short: string; color: string; verb: string }>> = {
  poison: { label: 'Empoisonné', short: 'PSN', color: '#a855f7', verb: 'est empoisonné' },
  burn: { label: 'Brûlé', short: 'BRL', color: '#f97316', verb: 'est brûlé' },
  paralysis: { label: 'Paralysé', short: 'PAR', color: '#eab308', verb: 'est paralysé' },
  sleep: { label: 'Endormi', short: 'SOM', color: '#94a3b8', verb: 's\'endort' },
  freeze: { label: 'Gelé', short: 'GEL', color: '#38bdf8', verb: 'est gelé' },
};
export const STATUS_KEYS = Object.keys(STATUSES) as StatusCondition[];

/** Statistiques modifiables en combat, par crans de -6 à +6. */
export type BattleStat = 'atk' | 'def' | 'spd' | 'accuracy' | 'evasion';
export const BATTLE_STAT_LABELS: Readonly<Record<BattleStat, string>> = {
  atk: 'l\'Attaque', def: 'la Défense', spd: 'la Vitesse', accuracy: 'la Précision', evasion: 'l\'Esquive',
};
export const BATTLE_STAT_KEYS = Object.keys(BATTLE_STAT_LABELS) as BattleStat[];
export const MAX_STAGE = 6;

/** Effet d'une attaque ; `chance` en pourcentage (100 = toujours). */
export type MoveEffect =
  /** Crans de statistique, sur le lanceur ou sur la cible. */
  | { type: 'stat'; target: 'self' | 'target'; stat: BattleStat; stages: number; chance: number }
  /** Statut infligé à la cible (sans effet si elle en a déjà un). */
  | { type: 'status'; status: StatusCondition; chance: number }
  /** Le lanceur récupère ce pourcentage de ses PV max. */
  | { type: 'heal'; percent: number }
  /** Le lanceur récupère ce pourcentage des dégâts infligés. */
  | { type: 'drain'; percent: number }
  /** Le lanceur subit ce pourcentage des dégâts infligés. */
  | { type: 'recoil'; percent: number }
  /** La cible, si elle n'a pas encore agi ce tour, a peur et ne peut pas attaquer. */
  | { type: 'flinch'; chance: number }
  /** Les dégâts laissent toujours au moins 1 PV à la cible (comme « Faux-Chage » dans Pokémon) : idéal avant une capture. */
  | { type: 'spare' };

export type MoveEffectType = MoveEffect['type'];

export const EFFECT_LABELS: Readonly<Record<MoveEffectType, string>> = {
  stat: 'Modifier une statistique', status: 'Infliger un statut', heal: 'Soigner le lanceur',
  drain: 'Drainer (vol de vie)', recoil: 'Contrecoup', flinch: 'Apeurer', spare: 'Épargner (laisse 1 PV)',
};

/** Coin supérieur gauche d'une image d'animation dans sa planche, en pixels. */
export interface AnimationFrame {
  x: number;
  y: number;
}

/**
 * Animation d'attaque, définie dans l'administration (onglet Animations) : des images de même taille
 * choisies une par une dans une planche d'effets téléversée, jouées dans l'ordre.
 */
export interface BattleAnimationInput {
  name: string;
  /** Planche d'effets (fichier téléversé, voir EFFECTS_URL). */
  image: string;
  /** Taille de chaque image, en pixels de la planche. */
  width: number;
  height: number;
  frames: AnimationFrame[];
  /** Durée d'une image, en millisecondes. */
  frameMs: number;
  /**
   * Image où l'attaque touche (indice dans `frames`) : la cible encaisse à cet instant, pendant que la suite de
   * l'animation se joue. null : à la fin de l'animation.
   */
  impactFrame: number | null;
}

export interface BattleAnimation extends BattleAnimationInput {
  id: number;
}

/** Animation jouée quand l'attaque est lancée. */
export interface MoveAnimation {
  animationId: number;
  /** Sur le monstre visé, ou sur le lanceur (soin, amélioration…). */
  on: 'target' | 'self';
}

export const BATTLE_ANIMATION_LIMITS = { maxFrames: 64, minMs: 20, maxMs: 1000, name: 40, maxSide: 1024 } as const;
/** Planches d'effets téléversées dans l'administration. */
export const EFFECTS_URL = '/uploads/effects/';
/** Fonds de combat téléversés dans l'administration (onglet Combats). */

export interface MoveInput {
  name: string;
  description: string;
  element: Element;
  category: MoveCategory;
  /** Puissance (0 pour une attaque de statut). */
  power: number;
  /** Précision en pourcentage, ou null : ne rate jamais. */
  accuracy: number | null;
  /** Énergie consommée à chaque utilisation (0 à ENERGY_MAX). */
  energy: number;
  /** Les priorités élevées passent avant, quelle que soit la vitesse (-3 à +3). */
  priority: number;
  /** Taux de coup critique élevé (1/8 au lieu de 1/16). */
  highCritical: boolean;
  effects: MoveEffect[];
  animation: MoveAnimation | null;
}

export interface Move extends MoveInput {
  id: number;
}

export const MOVE_LIMITS = { power: 250, priority: 3, effects: 6, name: 40, description: 300 } as const;

/**
 * Énergie : chaque monstre commence un combat avec ENERGY_MAX et chaque attaque en consomme. Sur le terrain, elle ne
 * revient pas toute seule : l'action « Repos », à la place d'une attaque, en rend ENERGY_REST_PERCENT %. En réserve
 * (dans l'équipe, pas K.O.), un monstre en récupère ENERGY_BENCH_PERCENT % à chaque tour.
 */
export const ENERGY_MAX = 100;
export const ENERGY_REST_PERCENT = 25;
export const ENERGY_BENCH_PERCENT = 5;
export const restEnergy = (maxEnergy = ENERGY_MAX): number => Math.ceil((maxEnergy * ENERGY_REST_PERCENT) / 100);
export const benchEnergy = (maxEnergy = ENERGY_MAX): number => Math.ceil((maxEnergy * ENERGY_BENCH_PERCENT) / 100);
export const MAX_MOVES = 4;

/** Attaque de secours, gratuite, d'un monstre qui ne connaît aucune attaque (comme « Lutte » dans Pokémon). */
export const STRUGGLE: Move = {
  id: 0, name: 'Lutte', description: 'Attaque désespérée d\'{un monstre} qui ne connaît aucune attaque : gratuite, mais blesse aussi le lanceur.',
  element: 'normal', category: 'damage', power: 40, accuracy: null, energy: 0, priority: 0, highCritical: false,
  effects: [{ type: 'recoil', percent: 25 }], animation: null,
};

/** Une ligne du tableau d'apprentissage d'une espèce. */
export interface LearnsetEntry {
  level: number;
  moveId: number;
}

/** Attaques que l'espèce peut connaître au niveau donné (chacune une fois, dans l'ordre d'apprentissage). */
export function learnableMoves(learnset: readonly LearnsetEntry[], level: number): number[] {
  const ids: number[] = [];
  for (const entry of [...learnset].sort((a, b) => a.level - b.level)) {
    if (entry.level <= level && !ids.includes(entry.moveId)) ids.push(entry.moveId);
  }
  return ids;
}

/** Attaques d'un monstre qui apparaît à ce niveau : les quatre dernières apprises. */
export const defaultMoves = (learnset: readonly LearnsetEntry[], level: number): number[] =>
  learnableMoves(learnset, level).slice(-MAX_MOVES);

/** Ce qu'il faut savoir d'une espèce pour suivre sa lignée d'évolution. */
export interface LineageSpecies {
  id: number;
  evolvesTo: number | null;
  evolveLevel: number | null;
  /** Ses propres attaques : celles qu'elle apprend sous cette forme (pas celles de ses pré-évolutions). */
  learnset: readonly LearnsetEntry[];
}

/** Une forme de la lignée, et les niveaux où un {monstre} l'a en évoluant : de `from` (inclus) à `until` (exclu ; null : sans limite). */
export interface LineageStage<S extends LineageSpecies> {
  species: S;
  from: number;
  until: number | null;
}

/**
 * Lignée d'une espèce, de sa première forme à elle-même. Chaque forme couvre les niveaux entre son apparition (le niveau
 * d'évolution de la précédente) et sa propre évolution ; la dernière, l'espèce elle-même, n'a pas de limite.
 */
export function lineageOf<S extends LineageSpecies>(species: S, all: readonly S[]): LineageStage<S>[] {
  const chain = [species];
  for (let current = species; ;) {
    const previous = all.find((other) => other.evolvesTo === current.id && other.evolveLevel !== null);
    // Une boucle d'évolutions (A → B → A) s'arrête au premier retour.
    if (!previous || chain.includes(previous)) break;
    chain.unshift(previous);
    current = previous;
  }
  return chain.map((stage, index) => ({
    species: stage,
    from: index === 0 ? 1 : (chain[index - 1]!.evolveLevel ?? 1),
    until: index === chain.length - 1 ? null : stage.evolveLevel,
  }));
}

/** Niveau à partir duquel l'espèce existe en faisant évoluer sa pré-évolution (1 : première forme). */
export const firstLevelOf = <S extends LineageSpecies>(species: S, all: readonly S[]): number => lineageOf(species, all).at(-1)?.from ?? 1;

/**
 * Attaques de l'espèce avec celles de sa lignée : chaque forme apporte celles qu'elle apprend entre son apparition et son
 * évolution. Une attaque de l'espèce sous son niveau d'apparition (jamais apprise) est ignorée. `speciesId` : la forme
 * qui l'apprend.
 */
export function lineageLearnset<S extends LineageSpecies>(species: S, all: readonly S[]): (LearnsetEntry & { speciesId: number })[] {
  return lineageOf(species, all)
    .flatMap(({ species: stage, from, until }) => stage.learnset
      .filter((entry) => entry.level >= from && (until === null || entry.level < until))
      .map((entry) => ({ ...entry, speciesId: stage.id })))
    .sort((a, b) => a.level - b.level);
}

/** Attaques apprises en passant du niveau `from` au niveau `to`. */
export function movesLearnedBetween(learnset: readonly LearnsetEntry[], from: number, to: number): number[] {
  const before = new Set(learnableMoves(learnset, from));
  return learnableMoves(learnset, to).filter((id) => !before.has(id));
}

/** Multiplicateur d'un cran de statistique (Attaque, Défense, Vitesse). */
export const stageMultiplier = (stage: number): number => (stage >= 0 ? (2 + stage) / 2 : 2 / (2 - stage));

/** Multiplicateur de précision (cran de Précision du lanceur moins cran d'Esquive de la cible). */
export const accuracyMultiplier = (stage: number): number => (stage >= 0 ? (3 + stage) / 3 : 3 / (3 - stage));

/** Résumé court des effets d'une attaque (menus, fiche). */
export function describeEffect(effect: MoveEffect): string {
  const chance = 'chance' in effect && effect.chance < 100 ? ` (${effect.chance} %)` : '';
  switch (effect.type) {
    case 'stat': {
      const sign = effect.stages > 0 ? `+${effect.stages}` : String(effect.stages);
      return `${sign} ${BATTLE_STAT_LABELS[effect.stat].replace(/^l'|^la /, '')} ${effect.target === 'self' ? 'du lanceur' : 'de la cible'}${chance}`;
    }
    case 'status': return `${STATUSES[effect.status].label}${chance}`;
    case 'heal': return `Soigne ${effect.percent} % des PV`;
    case 'drain': return `Draine ${effect.percent} % des dégâts`;
    case 'recoil': return `Contrecoup ${effect.percent} %`;
    case 'flinch': return `Apeure${chance}`;
    case 'spare': return 'Ne met jamais K.O. (laisse 1 PV)';
  }
}
